import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  orderBy,
  limit as limitQuery,
  serverTimestamp,
  setDoc,
  startAfter
} from 'firebase/firestore';
import { db } from './firebase';
import { auth } from './firebase';
import { prepareDateForFirestore } from '../utils/dateUtils';

// Devices
export const deviceService = {
  create: async (data) => {
    try {
      const deviceData = {
        ...data,
        userId: auth.currentUser.uid,
        entryDate: serverTimestamp(),
        deliveryDate: prepareDateForFirestore(data.deliveryDate),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(
        collection(db, `users/${auth.currentUser.uid}/devices`), 
        deviceData
      );
      return { id: docRef.id, ...deviceData };
    } catch (error) {
      console.error('Error creating device:', error);
      throw error;
    }
  },

  getAll: async (filters = {}, lastDoc = null, pageSize = 20) => {
    try {
      const userId = auth.currentUser.uid;
      let q = collection(db, `users/${userId}/devices`);
      
      let queryConstraints = [];
      
      if (filters.status) {
        queryConstraints.push(where('status', '==', filters.status));
        queryConstraints.push(orderBy('status'));
        queryConstraints.push(orderBy('createdAt', 'desc'));
      } else {
        queryConstraints.push(orderBy('createdAt', 'desc'));
      }
      
      if (filters.startDate && filters.endDate) {
        queryConstraints = [
          where('createdAt', '>=', filters.startDate),
          where('createdAt', '<=', filters.endDate),
          orderBy('createdAt', 'desc')
        ];
      }
      
      if (lastDoc) {
        queryConstraints.push(startAfter(lastDoc));
      }
      
      queryConstraints.push(limitQuery(pageSize));
      
      q = query(q, ...queryConstraints);
      
      const snapshot = await getDocs(q);
      let devices = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        devices = devices.filter(device => 
          device.repairNumber?.toString().includes(searchTerm) ||
          device.brand?.toLowerCase().includes(searchTerm) ||
          device.model?.toLowerCase().includes(searchTerm) ||
          device.serialNumber?.toLowerCase().includes(searchTerm) ||
          device.customerPhone?.includes(searchTerm)
        );
      }

      return {
        devices,
        lastDoc: snapshot.docs[snapshot.docs.length - 1],
        hasMore: snapshot.docs.length === pageSize
      };
    } catch (error) {
      console.error('Error fetching devices:', error);
      throw error;
    }
  },

  update: async (id, data) => {
    try {
      const userId = auth.currentUser.uid;
      const docRef = doc(db, `users/${userId}/devices/${id}`);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Device not found');
      }

      const updateData = {
        ...data,
        deliveryDate: data.deliveryDate instanceof Date ? data.deliveryDate : null,
        updatedAt: serverTimestamp()
      };

      await updateDoc(docRef, updateData);
      return { id, ...data };
    } catch (error) {
      console.error('Error updating device:', error);
      throw error;
    }
  },

  delete: async (id) => {
    try {
      const userId = auth.currentUser.uid;
      const docRef = doc(db, `users/${userId}/devices/${id}`);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Device not found');
      }

      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error('Error deleting device:', error);
      throw error;
    }
  }
};

// Inventory
export const inventoryService = {
  create: async (data) => {
    try {
      if (!auth.currentUser) {
        throw new Error('User not authenticated');
      }

      const inventoryData = {
        ...data,
        userId: auth.currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(
        collection(db, 'users', auth.currentUser.uid, 'inventory'), 
        inventoryData
      );
      return { id: docRef.id, ...inventoryData };
    } catch (error) {
      console.error('Error creating inventory item:', error);
      throw error;
    }
  },

  getAll: async () => {
    try {
      if (!auth.currentUser) {
        throw new Error('User not authenticated');
      }

      const q = query(
        collection(db, 'users', auth.currentUser.uid, 'inventory'),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting inventory:', error);
      if (error.code === 'permission-denied') {
        throw new Error('Non hai i permessi per accedere all\'inventario');
      }
      throw error;
    }
  },

  update: async (id, data) => {
    try {
      if (!auth.currentUser) {
        throw new Error('User not authenticated');
      }

      const docRef = doc(db, 'users', auth.currentUser.uid, 'inventory', id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Ricambio non trovato');
      }

      const newQuantity = Math.max(0, data.quantity);
      
      await updateDoc(docRef, {
        ...data,
        quantity: newQuantity,
        updatedAt: serverTimestamp()
      });

      return { id, ...data, quantity: newQuantity };
    } catch (error) {
      console.error('Error updating inventory item:', error);
      throw error;
    }
  },

  delete: async (id) => {
    try {
      if (!auth.currentUser) {
        throw new Error('User not authenticated');
      }

      const docRef = doc(db, 'users', auth.currentUser.uid, 'inventory', id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Ricambio non trovato');
      }

      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error('Error deleting inventory item:', error);
      throw error;
    }
  }
};

// Settings
export const settingsService = {
  get: async (userId) => {
    try {
      const docRef = doc(db, `users/${userId}/settings/default`);
      const docSnap = await getDoc(docRef);
      
      const defaultSettings = {
        companyName: '',
        address: '',
        phone: '',
        warrantyDays: 30,
        receiptWidth: '58mm',
        currentReceiptNumber: 1,
        autoPrint: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      if (!docSnap.exists()) {
        await setDoc(docRef, defaultSettings);
        return { id: userId, ...defaultSettings };
      }

      return { id: docSnap.id, ...docSnap.data() };
    } catch (error) {
      console.error('Error getting settings:', error);
      throw error;
    }
  },

  update: async (userId, data) => {
    try {
      const docRef = doc(db, `users/${userId}/settings/default`);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        await setDoc(docRef, {
          ...data,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } else {
        await updateDoc(docRef, {
          ...data,
          updatedAt: serverTimestamp()
        });
      }

      return { id: userId, ...data };
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  }
}; 